import { Reveal, Overline } from "@/components/common/ui";
import { Check, Star } from "lucide-react";
import { mediaUrl as M } from "@/data/pdpMedia";
import { txt } from "@/data/siteText";

const GoogleG = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z" />
  </svg>
);

const CHANGES = [
  "Sun spots & pigmentation visibly faded",
  "A more even, uniform skin tone",
  "Smoother texture and softer lines",
  "A brighter, healthier-looking glow",
];

export const Transformations = () => (
  <section className="bg-[#FFF0E8] py-24 md:py-32" data-testid="transformations">
    <div className="mx-auto grid max-w-[1180px] items-center gap-12 px-6 md:px-12 lg:grid-cols-[0.82fr_1fr] lg:gap-16">
      {/* Photo */}
      <Reveal className="mx-auto w-full max-w-[340px]">
        <div className="relative overflow-hidden rounded-[2rem] border border-[#F2E8E6] shadow-[0_40px_90px_-44px_rgba(120,80,55,0.5)]" data-testid="transformation-image">
          <img src="https://static.prod-images.emergentagent.com/jobs/6e9e37ac-1f04-43a6-819a-203cd06632b4/images/d5761b722a4cc152ea481b73d2a9e57a426e5a274718d2dc63238192b29a51cb.png" alt="Customer sun spots and pigmentation fading after consistent light therapy" className="block w-full" />
          <span className="absolute left-4 top-4 rounded-full bg-[#F2E8E6] px-3 py-1 text-[0.58rem] font-semibold uppercase tracking-[0.2em] text-[#232222]">Before</span>
          <span className="absolute bottom-4 left-4 rounded-full bg-[#F2E8E6] px-3 py-1 text-[0.58rem] font-semibold uppercase tracking-[0.2em] text-[#232222]">After · 8 weeks</span>
          {/* Highlight ring — main spot, before vs after */}
          <span style={{ left: "36%", top: "26%" }} className="pointer-events-none absolute h-24 w-24 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-dashed border-white/90 shadow-[0_0_0_1.5px_rgba(244,169,138,0.9)] sm:h-28 sm:w-28" data-testid="ring-before" />
          <span style={{ left: "36%", top: "74%" }} className="pointer-events-none absolute h-24 w-24 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-dashed border-white/90 shadow-[0_0_0_1.5px_rgba(244,169,138,0.9)] sm:h-28 sm:w-28" data-testid="ring-after" />
        </div>
        <p className="mt-4 text-center text-[0.7rem] font-light text-[#8A7F86]" data-text-slot="results_caption">{txt("results_caption", "With consistent use, many customers notice visible improvements in skin tone, texture and overall radiance over 8 to 12 weeks. Individual results may vary.")}</p>
      </Reveal>

      {/* Story */}
      <Reveal delay={0.1}>
        <Overline>Real Glow Stories</Overline>
        <h2 className="mt-3 font-display text-4xl font-light leading-[1.1] text-[#2C2B2C]">
          See the change. <span className="text-[#E97C6A]">Feel the difference.</span>
        </h2>
        <p className="mt-4 max-w-md text-base font-light leading-relaxed text-[#6E6168]">
          Marion, 58, used the Leopard Luxe mask 10 minutes a night. Eight weeks later, her fine
          lines and stubborn sun spots had visibly softened.
        </p>

        <ul className="mt-6 space-y-2.5">
          {CHANGES.map((c) => (
            <li key={c} className="flex items-center gap-3">
              <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-[#FBC4A8] text-[#FFFFFF]">
                <Check className="h-3.5 w-3.5" strokeWidth={3} />
              </span>
              <span className="text-sm text-[#2C2B2C]">{c}</span>
            </li>
          ))}
        </ul>

        <figure className="mt-7 rounded-2xl border border-[#EDE6DC] bg-white p-4 shadow-[0_18px_50px_-30px_rgba(120,80,55,0.4)]" data-testid="google-review-card">
          <div className="flex items-center gap-2">
            <span className="block text-[0.82rem] font-medium text-[#2C2B2C]">Marion T.</span>
            <div className="ml-auto flex items-center gap-2">
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => <Star key={i} className="h-3.5 w-3.5 text-[#FBBC05]" fill="currentColor" />)}
              </div>
              <GoogleG className="h-4 w-4 flex-none" />
            </div>
          </div>
          <blockquote className="mt-2.5 text-[0.8rem] font-light leading-relaxed text-[#3A3438]">
            "I'm 58 and I've tried everything. Eight weeks in, the fine lines around my eyes have genuinely softened and my skin looks lit from within."
          </blockquote>
          <figcaption className="mt-2.5 flex items-center gap-1.5 text-[0.68rem] font-light text-[#8A7F86]">
            <Check className="h-3 w-3 text-[#34A853]" strokeWidth={3} />
            Verified purchase · QLD, Australia · a month ago
          </figcaption>
        </figure>
      </Reveal>
    </div>
  </section>
);

const TIMELINE = [
  ["Week 1", "Skin feels calmer, softer and more hydrated, with that first post-session glow."],
  ["Week 4", "Many people start to notice a brighter, more even tone and fewer breakouts."],
  ["Week 8", "With consistent use, skin often looks firmer, smoother and more radiant."],
];

export const ResultsTimeline = () => (
  <section className="bg-[#FFFCFA] py-24 md:py-32" data-testid="results-timeline">
    <div className="mx-auto max-w-[1100px] px-6 md:px-12">
      <SectionHeading align="center" overline="What to Expect"
        title={<>Your glow, <span className="text-[#E97C6A]">week by week.</span></>}
        sub="Consistency is everything. Here's the kind of journey many in our community describe, though everyone's skin is different." className="mb-14" />
      <div className="grid gap-8 md:grid-cols-3">
        {TIMELINE.map(([week, text], i) => (
          <Reveal key={week} delay={i * 0.1} className="relative rounded-[2rem] border border-[#F2E8E6] bg-white p-8">
            <span className="font-display text-5xl font-light text-[#FBC4A8]">0{i + 1}</span>
            <h3 className="mt-3 font-display text-2xl text-[#2C2B2C]">{week}</h3>
            <p className="mt-2 text-sm font-light leading-relaxed text-[#8A7F86]">{text}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
